<template>
  <SidePanel :open="open" :title="title" @close="$emit('close')">
    <template v-if="sorted.length === 0">
      <div class="np-empty">
        <div class="np-empty__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6.5 17 L17.5 17" />
            <path d="M7.5 17 L7.5 11 C7.5 8.2 9.5 5.5 12 5.5 C14.5 5.5 16.5 8.2 16.5 11 L16.5 17" />
            <path d="M10.2 19 C10.2 20.1 11 21 12 21 C13 21 13.8 20.1 13.8 19" />
          </svg>
        </div>
        <p class="np-empty__copy">No notifications yet. We'll let you know when something needs your attention.</p>
      </div>
    </template>

    <template v-else>
      <ul class="np-list">
        <li
          v-for="n in sorted"
          :key="n.id"
          class="np-item"
          :class="[`np-item--${n.severity}`, { 'np-item--read': n.read }]"
        >
          <div class="np-item__icon" :class="`np-item__icon--${n.severity}`" aria-hidden="true">
            <!-- info -->
            <svg v-if="n.severity === 'info'" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9.5" />
              <path d="M12 11 L12 16.5" />
              <circle cx="12" cy="7.8" r="0.7" fill="currentColor" stroke="none" />
            </svg>
            <!-- success -->
            <svg v-else-if="n.severity === 'success'" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9.5" />
              <path d="M7.8 12.4 L10.8 15.4 L16.2 9.2" />
            </svg>
            <!-- warning -->
            <svg v-else-if="n.severity === 'warning'" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 4 L21 19.2 L3 19.2 Z" />
              <path d="M12 10 L12 14.4" />
              <circle cx="12" cy="16.6" r="0.7" fill="currentColor" stroke="none" />
            </svg>
            <!-- error -->
            <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9.5" />
              <path d="M9 9 L15 15" />
              <path d="M15 9 L9 15" />
            </svg>
          </div>
          <button
            class="np-item__dismiss"
            @click="dismiss(n.id)"
            aria-label="Dismiss notification"
            title="Dismiss"
          >
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
              <path d="M6 6 L18 18" />
              <path d="M18 6 L6 18" />
            </svg>
          </button>
          <div class="np-item__content">
            <h3 class="np-item__title">{{ n.title }}</h3>
            <p v-if="n.body" class="np-item__body">{{ n.body }}</p>
            <div class="np-item__meta">
              <span class="np-item__time">{{ relativeTime(n.createdAt) }}</span>
              <a
                v-if="n.actionLabel && n.actionUrl"
                :href="n.actionUrl"
                class="np-item__action"
                @click="onActionClick($event, n)"
              >
                <span>{{ n.actionLabel }}</span>
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M5 12 L19 12" />
                  <path d="M13 6 L19 12 L13 18" />
                </svg>
              </a>
            </div>
          </div>
        </li>
      </ul>
    </template>

    <template #footer>
      <div class="np-footer">
        <button
          class="np-footer__btn"
          @click="openSettings"
          title="Choose which campaign events alert you"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          <span>Settings</span>
        </button>
        <div class="np-footer__group">
          <button
            class="np-footer__btn"
            :disabled="!hasUnread"
            @click="markAllRead"
          >Mark all as read</button>
          <button
            class="np-footer__btn"
            :disabled="sorted.length === 0"
            @click="clear"
          >Clear all</button>
        </div>
      </div>
    </template>
  </SidePanel>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore, type AppNotification } from '@/store/notifications.store'
import SidePanel from './SidePanel.vue'

export default defineComponent({
  name: 'NotificationsPanel',
  components: { SidePanel },
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const store = useNotificationsStore()
    const router = useRouter()

    const sorted = computed(() => store.sorted)
    const hasUnread = computed(() => store.hasUnread)
    const unreadCount = computed(() => store.unreadCount)

    const title = computed(() =>
      unreadCount.value > 0 ? `Notifications (${unreadCount.value})` : 'Notifications'
    )

    function relativeTime(iso: string): string {
      const ms = Date.now() - new Date(iso).getTime()
      const secs = Math.max(0, Math.floor(ms / 1000))
      if (secs < 60) return 'just now'
      const mins = Math.floor(secs / 60)
      if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`
      const hrs = Math.floor(mins / 60)
      if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`
      const days = Math.floor(hrs / 24)
      if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
      const weeks = Math.floor(days / 7)
      return `${weeks} week${weeks === 1 ? '' : 's'} ago`
    }

    function dismiss(id: string) {
      store.dismiss(id)
    }
    function markAllRead() {
      store.markAllRead()
    }
    function clear() {
      store.clear()
    }
    function openSettings() {
      store.openConfigModal()
    }
    function onActionClick(e: MouseEvent, n: AppNotification) {
      // intercept normal-link click for in-app navigation, mark read
      if (n.actionUrl && n.actionUrl.startsWith('/')) {
        e.preventDefault()
        store.markRead(n.id)
        emit('close')
        router.push(n.actionUrl)
      }
    }

    // Mark items as read when the panel opens (on a small delay so the user sees the unread state briefly)
    watch(
      () => props.open,
      isOpen => {
        if (isOpen) {
          setTimeout(() => {
            // only mark read those that were unread at open-time, leave new ones unread
            store.markAllRead()
          }, 1200)
        }
      }
    )

    return {
      sorted,
      hasUnread,
      title,
      relativeTime,
      dismiss,
      markAllRead,
      clear,
      openSettings,
      onActionClick,
    }
  },
})
</script>

<style scoped>
.np-empty {
  text-align: center;
  padding: 48px 16px;
  color: rgba(255, 255, 255, 0.55);
}
.np-empty__icon {
  margin-bottom: 12px;
  opacity: 0.45;
  display: flex;
  justify-content: center;
}
.np-empty__copy {
  font-size: 13px;
  font-weight: 300;
  line-height: 1.5;
  max-width: 260px;
  margin: 0 auto;
}

.np-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Stat-colour palette — green/yellow/red as importance indicators per IRL2-2874 */
.np-list {
  --np-c-success: var(--vt-c-accent, #96ffc9);
  --np-c-success-rgb: var(--vt-c-accent-rgb, 150, 255, 201);
  --np-c-warning: #ffd76b;
  --np-c-warning-rgb: 255, 215, 107;
  --np-c-error: var(--vt-c-text-close, #fa9696);
  --np-c-error-rgb: 250, 150, 150;
  --np-c-info: rgba(255, 255, 255, 0.85);
  --np-c-info-rgb: 255, 255, 255;
}

.np-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 36px 14px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border-left: 3px solid transparent;
  transition: background 0.12s ease;
}

.np-item:hover {
  background: rgba(255, 255, 255, 0.07);
}

.np-item--info    { border-left-color: var(--np-c-info); }
.np-item--success { border-left-color: var(--np-c-success); }
.np-item--warning { border-left-color: var(--np-c-warning); }
.np-item--error   { border-left-color: var(--np-c-error); }

.np-item--read .np-item__title,
.np-item--read .np-item__body,
.np-item--read .np-item__time {
  color: rgba(255, 255, 255, 0.55);
}

.np-item__icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.np-item__icon--info    { background: rgba(var(--np-c-info-rgb), 0.14);    color: var(--np-c-info); }
.np-item__icon--success { background: rgba(var(--np-c-success-rgb), 0.2);  color: var(--np-c-success); }
.np-item__icon--warning { background: rgba(var(--np-c-warning-rgb), 0.22); color: var(--np-c-warning); }
.np-item__icon--error   { background: rgba(var(--np-c-error-rgb), 0.22);   color: var(--np-c-error); }

.np-item__content {
  flex: 1;
  min-width: 0;
}

.np-item__title {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.35;
}

.np-item__dismiss {
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  padding: 0;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.12s ease, background 0.12s ease;
}
.np-item__dismiss:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.08);
}

.np-item__body {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.5;
}

.np-item__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  font-weight: 400;
}

.np-item__time {
  color: rgba(255, 255, 255, 0.6);
}

.np-item__action {
  color: var(--vt-c-accent, #96ffc9);
  text-decoration: none;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.np-item__action:hover {
  text-decoration: underline;
}
.np-item__action svg {
  flex-shrink: 0;
}

/* Footer — three equal-size, solid pastel-green buttons per IRL2-2874 */
.np-footer {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  align-items: stretch;
}
.np-footer__group {
  display: contents;
}
.np-footer__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--vt-c-accent, #96ffc9);
  border: 1px solid var(--vt-c-accent, #96ffc9);
  color: var(--vt-c-background-primary, #181b2f);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: var(--font-sans);
  text-align: center;
  line-height: 1.2;
  transition: background 0.12s ease, transform 0.12s ease;
}
.np-footer__btn:hover:not(:disabled) {
  background: var(--vt-c-accent-light, #b4fcda);
  border-color: var(--vt-c-accent-light, #b4fcda);
}
.np-footer__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.np-footer__btn svg {
  flex-shrink: 0;
}
</style>
